import { useState } from "react";
import axios from "axios";
import { HeartPulse, RefreshCw, AlertTriangle, Mail } from "lucide-react";
import { Section, Kpi } from "@/mockups/dc";
import { RAPI, useApi, act, when, Rag, Th } from "./common";

const Mailbox = ({ connected }) => {
  const [kind, setKind] = useState("welcome");
  const [doc, setDoc] = useState("");
  const preview = (k) => { setKind(k); axios.get(`${RAPI}/email-preview/${k}`).then((r) => setDoc(r.data.html)); };
  const connect = () => act(() => axios.get(`${RAPI}/app-mailbox/connect`)).then((d) => d && (window.location.href = d.authorize_url));
  return (
    <Section title="System emails" sub="Welcome, invites, approvals, resets and alerts. Sent from the FourPawIQ app mailbox once connected; until then they go out the way they do today." icon={Mail} testid="health-mailbox"
      right={<span className={`badge ${connected ? "pos" : "warn"}`} data-testid="health-mailbox-status">{connected ? "App mailbox connected" : "App mailbox not connected"}</span>}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        <button className="btn btn-sm" onClick={connect} data-testid="health-mailbox-connect">{connected ? "Reconnect" : "Connect"} Microsoft 365 mailbox</button>
        {["welcome", "invite", "alert"].map((k) => <button key={k} className={`btn btn-sm ${doc && kind === k ? "btn-primary" : ""}`} onClick={() => preview(k)} data-testid={`health-preview-${k}`}>Preview {k}</button>)}
        <button className="btn btn-sm" onClick={() => act(() => axios.post(`${RAPI}/app-mailbox/test`, { kind }), (d) => `Test sent to ${d.to}`)} data-testid="health-mailbox-test">Send test {kind} to me</button>
      </div>
      {doc && <iframe title="Email preview" srcDoc={doc} style={{ width: "100%", height: 640, border: "1px solid var(--line)", borderRadius: 8 }} data-testid="health-email-frame" />}
    </Section>
  );
};

export const HealthTab = () => {
  const [d, reload] = useApi("/health");
  if (!d) return <div className="skeleton" style={{ height: 240, borderRadius: 12 }} />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
        <Kpi label="Green" value={d.counts.green} testid="health-kpi-green" />
        <Kpi label="Amber" value={d.counts.amber} status={d.counts.amber ? "warn" : undefined} testid="health-kpi-amber" />
        <Kpi label="Red" value={d.counts.red} status={d.counts.red ? "neg" : undefined} testid="health-kpi-red" />
        <Kpi label="Gingr location issues" value={d.partition_issues.length} status={d.partition_issues.length ? "neg" : undefined} sub="shared Gingr accounts" testid="health-kpi-partition" />
      </div>
      {d.partition_issues.length > 0 && (
        <div className="alert" style={{ alignItems: "flex-start" }} data-testid="health-partition-issues">
          <AlertTriangle size={16} style={{ flexShrink: 0 }} />
          <div><b>These locations could see each other's data.</b> Pick the right Gingr location for each one.
            <ul style={{ margin: "6px 0 0 16px", listStyle: "disc", fontSize: 12 }}>{d.partition_issues.map((i, k) => <li key={k}>{i.location} ({i.app_url}): {i.issue}</li>)}</ul></div>
        </div>
      )}
      <Section title="Health board" sub="Gingr connection, data syncs and agents for every location. The daily email covers anything red or amber." icon={HeartPulse} testid="health-board"
        right={<button className="btn btn-sm" onClick={() => act(() => axios.post(`${RAPI}/health/check`), (r) => r.message, () => setTimeout(reload, 30000))} data-testid="health-check-all"><RefreshCw size={12} />Check all Gingr keys now</button>}>
        <div style={{ overflowX: "auto" }}>
          <table className="table" style={{ width: "100%" }}>
            <Th cols={["Location", "Status", "Gingr", "Data sync", "Agents"]} />
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.location_id} data-testid={`health-row-${r.location_id}`}>
                  <td><b>{r.name}</b>{r.status !== "active" && <span className="badge neutral" style={{ marginLeft: 6 }}>{r.status}</span>}</td>
                  <td><Rag c={r.rag} testid={`health-rag-${r.location_id}`} /></td>
                  <td style={{ fontSize: 12 }}>{!r.gingr.connected ? <span className="muted">Not connected</span> : r.gingr.status === "failing"
                    ? <span style={{ color: "var(--neg-ink)" }}>Failing since {when(r.gingr.last_fail)}<div style={{ fontSize: 11 }}>{r.gingr.error}</div></span>
                    : <>OK{r.gingr.last_ok ? ` · checked ${when(r.gingr.last_ok)}` : ""}</>}</td>
                  <td style={{ fontSize: 12 }}>{r.sync.status === "error" ? <span style={{ color: "var(--neg-ink)" }}>Failed: {(r.sync.error || "").slice(0, 120)}</span>
                    : r.sync.last_sync ? <>{when(r.sync.last_sync)} · <span className="mono">{(r.sync.customers || 0).toLocaleString()}</span> customers</> : <span className="muted">Never synced</span>}</td>
                  <td style={{ fontSize: 12 }}>
                    {r.agents.last_ok && <div>Last OK {when(r.agents.last_ok.at)} <span className="muted">({r.agents.last_ok.agent})</span></div>}
                    {r.agents.last_fail && <div style={{ color: "var(--neg-ink)" }}>Last failure {when(r.agents.last_fail.at)} <span>({r.agents.last_fail.agent})</span></div>}
                    {!r.agents.last_ok && !r.agents.last_fail && <span className="muted">No runs in 14 days</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <Mailbox connected={d.app_mailbox} />
    </div>
  );
};
